import { useMemo } from 'react';

import type { GridInitialState } from '@mui/x-data-grid';

import { isDefined, nameof } from '@e-socna/utils';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { ResolverReturnTypeId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import type { ReferencesContext_Resolver } from '../../../contexts/ReferencesContext';

import { RESOLVER_RETURN_TYPE } from '../get-columns';

interface Params {
  readonly allowedReturnTypeIds?: readonly ResolverReturnTypeId[];
  readonly mustBeResolvableOnBack?: boolean;
  readonly mustBeResolvableOnFront?: boolean;
}

export const useInitialGridState = (
  {
    allowedReturnTypeIds,
    mustBeResolvableOnBack,
    mustBeResolvableOnFront,
  }: Params = {
    allowedReturnTypeIds: undefined,
    mustBeResolvableOnBack: undefined,
    mustBeResolvableOnFront: undefined,
  },
) => {
  const { resolverReturnTypesIdMap } = useReferencesContext();

  const initialGridState = useMemo<GridInitialState>(
    () => ({
      columns: {
        columnVisibilityModel: {
          [nameof<ReferencesContext_Resolver>('isResolvableOnFront')]: false,
          [nameof<ReferencesContext_Resolver>('isResolvableOnBack')]: false,
        },
      },
      filter: {
        filterModel: {
          items: [
            ...(allowedReturnTypeIds?.length
              ? [
                  {
                    id: RESOLVER_RETURN_TYPE,
                    field: RESOLVER_RETURN_TYPE,
                    operator: 'isAnyOf',
                    value: allowedReturnTypeIds
                      .map(
                        (returnTypeId) =>
                          resolverReturnTypesIdMap[returnTypeId]?.code,
                      )
                      .filter(isDefined),
                  },
                ]
              : EMPTY_ARRAY),
            ...(mustBeResolvableOnBack
              ? [
                  {
                    id: nameof<ReferencesContext_Resolver>(
                      'isResolvableOnBack',
                    ),
                    field:
                      nameof<ReferencesContext_Resolver>('isResolvableOnBack'),
                    operator: 'is',
                    value: mustBeResolvableOnBack,
                  },
                ]
              : EMPTY_ARRAY),
            ...(mustBeResolvableOnFront
              ? [
                  {
                    id: nameof<ReferencesContext_Resolver>(
                      'isResolvableOnFront',
                    ),
                    field: nameof<ReferencesContext_Resolver>(
                      'isResolvableOnFront',
                    ),
                    operator: 'is',
                    value: mustBeResolvableOnFront,
                  },
                ]
              : EMPTY_ARRAY),
          ],
        },
      },
    }),
    [
      allowedReturnTypeIds,
      mustBeResolvableOnBack,
      mustBeResolvableOnFront,
      resolverReturnTypesIdMap,
    ],
  );

  return { initialGridState };
};
